import PropTypes from 'prop-types';
import { forwardRef, useEffect, useMemo, useState } from "react";
import { HiSparkles } from "react-icons/hi";
import { IoChevronDown, IoChevronUp } from "react-icons/io5";
import { MdAutorenew } from "react-icons/md";
import { buildApiUrl, ENDPOINTS } from "../../../config/api";
import { PREDEFINED_USE_CASES } from "../../../config/constants";
import useStreamingSummary from "../../../hooks/useStreamingSummary";
import {
    applySuggestion,
    enhanceUseCase,
    extractMetadata,
    getSuggestions,
} from "../../../services/api";
import { apiJson } from "../../../services/httpClient";
import { summaryStorage } from "../../../services/summaryStorage";
import RichTextEditor from "../../editor/RichTextEditor";
import FeedbackSection from "../../feedback/FeedbackSection";
import ContentOptionsRibbon from "../ContentOptionsRibbon";
import FeatureRibbon from "../FeatureRibbon";
import styles from "./ResultsSection.module.css";

const ResultsSection = forwardRef(({
  summaryResult,
  setSummaryResult,
  textInput,
  workTitle,
  selectedMethod,
  temperature,
  topP,
  selectedModel,
  isLoading,
  setIsLoading,
  intendedAudience,
  summaryStyle,
  uploadedFile,
  selectedPrompt,
  useCase,
  currentStep = 3,
  onBack,
}, ref) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [processingStage, setProcessingStage] = useState("");
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);

  const isActive = currentStep === 3;
  const isCompleted = currentStep > 3;
  const isDisabled = currentStep < 3;

  // Auto-expand/collapse based on step
  useEffect(() => {
    if (isActive) {
      setIsExpanded(true);
    } else if (isCompleted) {
      setIsExpanded(false);
    }
  }, [isActive, isCompleted]);
  const [originalTextInput, setOriginalTextInput] = useState("");
  const [includeGlossary, setIncludeGlossary] = useState(false);
  const [includeQuote, setIncludeQuote] = useState(false);
  const [includeCitation, setIncludeCitation] = useState(false);
  const [includeMetadata, setIncludeMetadata] = useState(false);
  const [glossaryContent, setGlossaryContent] = useState("");
  const [quoteContent, setQuoteContent] = useState("");
  const [citationContent, setCitationContent] = useState("");
  const [metadata, setMetadata] = useState({
    researchers: [],
    title: "",
    dateWritten: "",
    tags: [],
  });
  const [isMetadataLoading, setIsMetadataLoading] = useState(false);
  const [isGlossaryLoading, setIsGlossaryLoading] = useState(false);
  const [isQuoteLoading, setIsQuoteLoading] = useState(false);
  const [isCitationLoading, setIsCitationLoading] = useState(false);

  // Citation panel state
  const [activeCitationId, setActiveCitationId] = useState(null);

  // Suggestion state
  const [suggestions, setSuggestions] = useState([]);
  const [isSuggestionsLoading, setIsSuggestionsLoading] = useState(false);
  const [applyingSuggestionId, setApplyingSuggestionId] = useState(null);

  // Parse citation data - citationContent is now a JSON object from backend
  const citationData = useMemo(() => {
    if (!citationContent || typeof citationContent !== 'object') {
      return [];
    }
    const allCitations = citationContent.citations || [];

    // Only keep citations whose summary_sentence actually appears in the response text
    const summaryText = (summaryResult[0] || '')
      .replace(/\*\*(.+?)\*\*/g, '$1')
      .replace(/\*(.+?)\*/g, '$1')
      .replace(/__(.+?)__/g, '$1')
      .replace(/_(.+?)_/g, '$1')
      .replace(/`(.+?)`/g, '$1')
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      .replace(/^[-*+]\s+/gm, '')
      .replace(/^\d+\.\s+/gm, '')
      .replace(/^>\s+/gm, '')
      .replace(/^#{1,6}\s+/gm, '')
      .toLowerCase();

    const data = allCitations.filter(c => {
      if (!c.summary_sentence || c.summary_sentence.length < 10) return false;
      const stripped = c.summary_sentence
        .replace(/\*\*(.+?)\*\*/g, '$1')
        .replace(/\*(.+?)\*/g, '$1')
        .replace(/__(.+?)__/g, '$1')
        .replace(/_(.+?)_/g, '$1')
        .replace(/`(.+?)`/g, '$1')
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
        .replace(/^[-*+]\s+/gm, '')
        .replace(/^\d+\.\s+/gm, '')
        .replace(/^>\s+/gm, '')
        .replace(/^#{1,6}\s+/gm, '')
        .replace(/\n+/g, ' ')
        .trim()
        .toLowerCase();
      return stripped && summaryText.includes(stripped);
    });

    return data;
  }, [citationContent, summaryResult]);

  // Handle citation click from editor
  const handleCitationClick = (citationId) => {
    if (activeCitationId === citationId) {
      setActiveCitationId(null);
    } else {
      setActiveCitationId(citationId);
    }
  };

  const requestOptionalContent = async ({
    includeGlossary: glossary = false,
    includeQuote: quote = false,
    includeCitation: citation = false,
  }) => {
    if (!summaryResult[0]) return;

    return apiJson(ENDPOINTS.SUMMARIZE, {
      method: "POST",
      body: JSON.stringify({
        text: summaryResult[0],
        source_text: originalTextInput || textInput,
        model: selectedModel,
        temperature,
        top_p: topP,
        method: selectedMethod,
        intended_audience: intendedAudience,
        summary_style: summaryStyle,
        include_glossary: glossary,
        include_quote: quote,
        include_citation: citation,
        prompt_key: selectedPrompt !== "auto" ? selectedPrompt : undefined,
        skip_generation: true,
      }),
    });
  };

  const handleRegenerateGlossary = async () => {
    if (!summaryResult[0]) return;
    setIsGlossaryLoading(true);
    try {
      const data = await requestOptionalContent({ includeGlossary: true });
      setGlossaryContent(data?.glossary || "");
    } catch (err) {
      console.error("Failed to regenerate glossary:", err);
    } finally {
      setIsGlossaryLoading(false);
    }
  };

  const handleRegenerateQuote = async () => {
    if (!summaryResult[0]) return;
    setIsQuoteLoading(true);
    try {
      const data = await requestOptionalContent({ includeQuote: true });
      setQuoteContent(data?.quote || "");
    } catch (err) {
      console.error("Failed to regenerate quote:", err);
    } finally {
      setIsQuoteLoading(false);
    }
  };

  const handleRegenerateCitations = async () => {
    if (!summaryResult[0]) return;
    setIsCitationLoading(true);
    try {
      const data = await requestOptionalContent({ includeCitation: true });
      setCitationContent(data?.citation || null);
    } catch (err) {
      console.error("Failed to regenerate citations:", err);
    } finally {
      setIsCitationLoading(false);
    }
  };

  const handleRegenerateMetadata = async () => {
    if (!summaryResult[0]) return;
    setIsMetadataLoading(true);
    try {
      const extractedMetadata = await extractMetadata(
        originalTextInput || textInput,
        summaryResult[0],
        {
          model: selectedModel,
          temperature,
          top_p: topP,
        }
      );
      setMetadata(extractedMetadata);
    } catch (err) {
      console.error("Failed to regenerate metadata:", err);
    } finally {
      setIsMetadataLoading(false);
    }
  };

  // ── Suggestion handlers ──
  const handleGetSuggestions = async () => {
    if (!summaryResult[0]) return;
    setIsSuggestionsLoading(true);
    setSuggestions([]);
    try {
      const result = await getSuggestions(summaryResult[0], {
        source_text: originalTextInput || textInput,
        intended_audience: intendedAudience,
        model: selectedModel,
        temperature,
      });
      setSuggestions(result.suggestions || []);
    } catch (err) {
      console.error("Failed to get suggestions:", err);
    } finally {
      setIsSuggestionsLoading(false);
    }
  };

  const handleApplySuggestion = async (suggestion) => {
    if (!summaryResult[0]) return;
    setApplyingSuggestionId(suggestion.id);

    // Illustrate suggestions: insert a placeholder directly, no LLM call
    if (suggestion.type === "illustrate") {
      const text = summaryResult[0];
      const placeholder = `< ${suggestion.title} illustration >`;
      let updatedText;

      if (suggestion.target_section) {
        // Insert placeholder after the target section heading
        const headingPattern = new RegExp(
          `(^#{1,6}\\s*.*${suggestion.target_section.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}.*$)`,
          "mi"
        );
        const match = text.match(headingPattern);
        if (match) {
          const idx = text.indexOf(match[0]) + match[0].length;
          updatedText = text.slice(0, idx) + "\n\n" + placeholder + text.slice(idx);
        } else {
          updatedText = text + "\n\n" + placeholder;
        }
      } else {
        updatedText = text + "\n\n" + placeholder;
      }

      setSummaryResult([updatedText]);
      setSuggestions((prev) => prev.filter((s) => s.id !== suggestion.id));
      setApplyingSuggestionId(null);
      return;
    }

    try {
      const result = await applySuggestion(
        summaryResult[0],
        suggestion,
        {
          source_text: originalTextInput || textInput,
          model: selectedModel,
          temperature,
        }
      );
      if (result.success && result.edited_text) {
        setSummaryResult([result.edited_text]);
        // Remove the applied suggestion from the list
        setSuggestions((prev) => prev.filter((s) => s.id !== suggestion.id));
      }
    } catch (err) {
      console.error("Failed to apply suggestion:", err);
    } finally {
      setApplyingSuggestionId(null);
    }
  };

  // Use streaming hook instead of local isStreaming state
  const { isStreaming, streamContent } = useStreamingSummary();

  const handleGenerate = async () => {
    if (!textInput) return;
    setOriginalTextInput(textInput);
    setSummaryResult([""]);
    setGlossaryContent("");
    setQuoteContent("");
    setCitationContent("");
    setProcessingStage("Processing");
    setShowSuccessAnimation(false);

    try {

      // For custom use cases, enhance the prompt first
      let useCasePrompt = undefined;
      const isCustomUseCase = useCase && !PREDEFINED_USE_CASES.includes(useCase);
      
      if (isCustomUseCase) {
        setProcessingStage("Enhancing prompt for custom use case");
        try {
          const enhanceResult = await enhanceUseCase(useCase, {
            audience: intendedAudience,
            style: summaryStyle,
            model: selectedModel,
          });
          if (enhanceResult.success && enhanceResult.enhanced_prompt) {
            useCasePrompt = enhanceResult.enhanced_prompt;
          }
        } catch (err) {
          console.warn("Failed to enhance use case prompt, falling back to default:", err);
        }
      }

      setProcessingStage("Analyzing request");
      const accumulatedText = await streamContent(
        buildApiUrl(ENDPOINTS.SUMMARIZE_STREAM),
        {
          text: textInput,
          model: selectedModel,
          temperature,
          top_p: topP,
          method: selectedMethod,
          intended_audience: intendedAudience,
          summary_style: summaryStyle,
          prompt_key: selectedPrompt !== "auto" ? selectedPrompt : undefined,
          use_case: useCase || undefined,
          use_case_prompt: useCasePrompt || undefined,
        },
        (displayText) => setSummaryResult([displayText]),
        (status) => {
          const label = status?.label;
          if (typeof label === 'string' && label.trim()) {
            setProcessingStage(label);
          }
        }
      );

      setProcessingStage("");
      setShowSuccessAnimation(true);

      setTimeout(() => setShowSuccessAnimation(false), 2000);

      if (includeGlossary || includeQuote || includeCitation) {
        if (includeGlossary) setIsGlossaryLoading(true);
        if (includeQuote) setIsQuoteLoading(true);
        if (includeCitation) setIsCitationLoading(true);

        const postData = await apiJson(ENDPOINTS.SUMMARIZE, {
          method: "POST",
          body: JSON.stringify({
            text: accumulatedText,
            source_text: originalTextInput || textInput, // Original source for citation matching
            model: selectedModel,
            temperature,
            top_p: topP,
            method: selectedMethod,
            intended_audience: intendedAudience,
            summary_style: summaryStyle,
            include_glossary: includeGlossary,
            include_quote: includeQuote,
            include_citation: includeCitation,
            prompt_key: selectedPrompt !== "auto" ? selectedPrompt : undefined,
            skip_generation: true,
          }),
        });
        setGlossaryContent(postData.glossary || "");
        setQuoteContent(postData.quote || "");

        // Citation is now a JSON object with citations array
        setCitationContent(postData.citation || null);
        setIsGlossaryLoading(false);
        setIsQuoteLoading(false);
        setIsCitationLoading(false);
      }

      // Extract metadata after summary is generated (if enabled)
      if (includeMetadata) {
        try {
          setIsMetadataLoading(true);
          const extractedMetadata = await extractMetadata(
            textInput,
            accumulatedText,
            {
              model: selectedModel,
              temperature,
              top_p: topP,
            }
          );
          setMetadata(extractedMetadata);
          setIsMetadataLoading(false);
        } catch (metadataError) {
          setIsMetadataLoading(false);
        }
      }
    } catch (error) {
      console.error("Error fetching summary:", error);
      setSummaryResult([]);
      setIsGlossaryLoading(false);
      setIsQuoteLoading(false);
      setIsCitationLoading(false);
      setProcessingStage("");
    }
  };

  const handleGlossaryChange = () => setIncludeGlossary(!includeGlossary);
  const handleQuoteChange = () => setIncludeQuote(!includeQuote);
  const handleCitationChange = () => setIncludeCitation(!includeCitation);
  const handleMetadataChange = () => setIncludeMetadata(!includeMetadata);

  const handleRegenerate = async (feedbackText, enhancePromptEnabled) => {
    if (!feedbackText || !originalTextInput) return;
    const currentSummary = summaryResult[0];
    let accumulatedText = "";
    setSummaryResult([""]);
    setGlossaryContent("");
    setQuoteContent("");
    setCitationContent("");

    try {
      accumulatedText = await streamContent(
        buildApiUrl(ENDPOINTS.REGENERATE_STREAM),
        {
          original_text: originalTextInput,
          current_summary: currentSummary,
          feedback: feedbackText,
          enhance_prompt: enhancePromptEnabled,
          include_glossary: includeGlossary,
          include_quote: includeQuote,
          include_citation: includeCitation,
          model: selectedModel,
          temperature,
          top_p: topP,
          intended_audience: intendedAudience,
          summary_style: summaryStyle,
        },
        (displayText) => setSummaryResult([displayText])
      );

      if (includeGlossary || includeQuote || includeCitation) {
        if (includeGlossary) setIsGlossaryLoading(true);
        if (includeQuote) setIsQuoteLoading(true);
        if (includeCitation) setIsCitationLoading(true);

        try {
          const postData = await apiJson(ENDPOINTS.REGENERATE, {
            method: "POST",
            body: JSON.stringify({
              original_text: originalTextInput,
              current_summary: accumulatedText,
              feedback: feedbackText,
              enhance_prompt: false,
              include_glossary: includeGlossary,
              include_quote: includeQuote,
              include_citation: includeCitation,
              model: selectedModel,
              temperature,
              top_p: topP,
              intended_audience: intendedAudience,
              summary_style: summaryStyle,
            }),
          });
          setGlossaryContent(postData.glossary || "");
          setQuoteContent(postData.quote || "");
          setCitationContent(postData.citation || null);
        } catch (postProcessError) {
          console.error("Error regenerating enhancement content:", postProcessError);
        } finally {
          setIsGlossaryLoading(false);
          setIsQuoteLoading(false);
          setIsCitationLoading(false);
        }
      }

      // Extract metadata after regeneration (if enabled)
      if (includeMetadata) {
        try {
          setIsMetadataLoading(true);
          const extractedMetadata = await extractMetadata(
            originalTextInput,
            accumulatedText,
            {
              model: selectedModel,
              temperature,
              top_p: topP,
            }
          );
          setMetadata(extractedMetadata);
          setIsMetadataLoading(false);
        } catch (metadataError) {
          setIsMetadataLoading(false);
        }
      }
    } catch (error) {
      console.error("Error regenerating summary:", error);
      if (!accumulatedText) {
        setSummaryResult([]);
      }
      setIsGlossaryLoading(false);
      setIsQuoteLoading(false);
      setIsCitationLoading(false);
    }
  };

  const handleSave = async () => {
    if (!summaryResult.length) return;

    try {
      // Determine base title
      let finalTitle = workTitle.trim() || "Untitled";

      // Check for duplicates
      const existingSummaries = await summaryStorage.getSummaries();

      // Filter for titles that match "Title" or "Title (n)" behavior
      // Note: This matches regardless of case to prevent confusion
      const pattern = new RegExp(`^${finalTitle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(\\s\\(\\d+\\))?$`, 'i');

      const duplicates = existingSummaries.filter(s => {
        const title = s.metadata?.custom_title || s.fileName || "Untitled";
        return pattern.test(title);
      });

      if (duplicates.length > 0) {
        // Find the next available number
        let maxNum = 0;
        let exactMatchFound = false;

        duplicates.forEach(s => {
          const title = s.metadata?.custom_title || s.fileName || "Untitled";
          if (title.toLowerCase() === finalTitle.toLowerCase()) {
            exactMatchFound = true;
          }

          const match = title.match(/\((\d+)\)$/);
          if (match) {
            const num = parseInt(match[1]);
            if (num > maxNum) maxNum = num;
          }
        });

        if (exactMatchFound || maxNum > 0) {
          finalTitle = `${finalTitle} (${maxNum + 1})`;
        }
      }

      const saved = await summaryStorage.saveSummary({
        summary: summaryResult[0],
        originalText: originalTextInput,
        method: selectedMethod,
        model: selectedModel,
        temperature,
        topP,
        intendedAudience,
        summaryStyle,
        fileName: uploadedFile?.name || null,
        fileSize: uploadedFile?.size || null,
        fileType: uploadedFile?.type || null,
        metadata: {
          ...metadata,
          custom_title: finalTitle
        }
      });

      if (saved) {
        // saved successfully
      }
    } catch (error) {
      console.error('Error saving summary:', error);
    }
  };

  // Prepare export data for the ExportButton component
  const getExportData = () => {
    if (!summaryResult.length) return {};

    return {
      summary: summaryResult[0],
      filename: `ai-summary-${selectedMethod.toLowerCase().replace(/\s+/g, '-')}`,
      metadata: metadata,
      glossary: glossaryContent,
      quote: quoteContent,
      citation: citationContent,
      originalText: originalTextInput,
      method: selectedMethod,
      model: selectedModel,
      temperature: temperature,
      topP: topP,
      intendedAudience: intendedAudience,
      summaryStyle: summaryStyle,
      fileName: uploadedFile?.name || null,
      fileSize: uploadedFile?.size || null,
      fileType: uploadedFile?.type || null,
    };
  };

  return (
    <div ref={ref} className={`${styles["results-section"]} ${isActive ? styles.active : ''
      } ${isCompleted ? styles.completed : ''} ${isDisabled ? styles.disabled : ''
      }`}>
      <div className={styles.sectionHeader}>
        <h2>
          Step 3: Generate
          {summaryResult.length > 0 && summaryResult[0] && <span className={styles.completedBadge}></span>}
        </h2>
        <button
          className={styles.collapseBtn}
          onClick={() => setIsExpanded(!isExpanded)}
          aria-label={isExpanded ? "Collapse section" : "Expand section"}
          disabled={isDisabled}
        >
          {isExpanded ? <IoChevronUp /> : <IoChevronDown />}
        </button>
      </div>

      {isExpanded && isActive && (
        <>
          <ContentOptionsRibbon
            includeGlossary={includeGlossary}
            includeQuote={includeQuote}
            includeCitation={includeCitation}
            includeMetadata={includeMetadata}
            onGlossaryChange={handleGlossaryChange}
            onQuoteChange={handleQuoteChange}
            onCitationChange={handleCitationChange}
            onMetadataChange={handleMetadataChange}
          />

          <div className={styles.actionButtonsContainer}>
            {onBack && (
              <button
                className={styles["backBtn"]}
                onClick={onBack}
                disabled={isLoading || isStreaming}
              >
                ← Back to Upload
              </button>
            )}

            <button
              className={styles["generateBtn"]}
              onClick={handleGenerate}
              disabled={isLoading || isStreaming || !textInput}
            >
              {isLoading || isStreaming ? (
                <>
                  <MdAutorenew className={styles.spinner} />
                  {processingStage || "Generating"}...
                </>
              ) : (
                <>
                  <HiSparkles className={styles.generateIcon} />
                  Generate Summary
                </>
              )}
            </button>
          </div>

          {summaryResult.length > 0 && (
            <div className={`${styles["results-container"]} ${showSuccessAnimation ? styles.successAnimation : ''
              }`}>
              <div className={styles["results-header"]}>
                <h3 className={styles["results-title"]}>Results</h3>
              </div>

              <div className={styles["results-body"]}>
                <div className={styles["results-main"]}>
                  <div className={`${styles["results-text"]} ${showSuccessAnimation ? styles.pulseFirstLine : ''
                    }`}>
                    <RichTextEditor
                      content={summaryResult[0] || ''}
                      onUpdate={(newContent) => {
                        const updated = [...summaryResult];
                        updated[0] = newContent;
                        setSummaryResult(updated);
                      }}
                      isStreaming={isStreaming}
                      citations={citationData}
                      onCitationClick={handleCitationClick}
                      onSave={handleSave}
                      exportData={getExportData()}
                      isLoading={isLoading}
                      hasSummary={summaryResult.length > 0}
                    />
                  </div>
                </div>

                <FeatureRibbon
                  includeMetadata={includeMetadata}
                  includeGlossary={includeGlossary}
                  includeQuote={includeQuote}
                  includeCitation={includeCitation}
                  metadata={metadata}
                  onMetadataUpdate={setMetadata}
                  onRegenerateMetadata={handleRegenerateMetadata}
                  isMetadataLoading={isMetadataLoading}
                  glossaryContent={glossaryContent}
                  onRegenerateGlossary={handleRegenerateGlossary}
                  isGlossaryLoading={isGlossaryLoading}
                  quoteContent={quoteContent}
                  onRegenerateQuote={handleRegenerateQuote}
                  isQuoteLoading={isQuoteLoading}
                  citationData={citationData}
                  isCitationLoading={isCitationLoading}
                  onRegenerateCitations={handleRegenerateCitations}
                  activeCitationId={activeCitationId}
                  onCitationSelect={setActiveCitationId}
                  onGetSuggestions={handleGetSuggestions}
                  isSuggestionsLoading={isSuggestionsLoading}
                  suggestions={suggestions}
                  onApplySuggestion={handleApplySuggestion}
                  applyingSuggestionId={applyingSuggestionId}
                />
              </div>

              <FeedbackSection
                onRegenerate={handleRegenerate}
                isLoading={isLoading}
                isStreaming={isStreaming}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
});

ResultsSection.propTypes = {
  summaryResult: PropTypes.arrayOf(PropTypes.string).isRequired,
  setSummaryResult: PropTypes.func.isRequired,
  textInput: PropTypes.string.isRequired,
  workTitle: PropTypes.string,
  selectedMethod: PropTypes.string.isRequired,
  temperature: PropTypes.number.isRequired,
  topP: PropTypes.number.isRequired,
  selectedModel: PropTypes.string.isRequired,
  isLoading: PropTypes.bool.isRequired,
  setIsLoading: PropTypes.func.isRequired,
  intendedAudience: PropTypes.string.isRequired,
  summaryStyle: PropTypes.string.isRequired,
  uploadedFile: PropTypes.object,
  selectedPrompt: PropTypes.string.isRequired,
  useCase: PropTypes.string,
  currentStep: PropTypes.number,
  onBack: PropTypes.func,
};

ResultsSection.displayName = 'ResultsSection';

export default ResultsSection;
